import React, { useMemo } from "react";

type Props = {
  color?: string;
  size?: number;
  active?: boolean;
  loop?: boolean;
  dropDurationMs?: number;
  spinDurationMs?: number;
};

const RestaurantScaleAnimation: React.FC<Props> = ({
  color = "#32008E",
  size = 240,
  active = true,
  loop = true,
  dropDurationMs = 2400,
}) => {
  const ids = useMemo(() => {
    const r = Math.random().toString(36).slice(2, 8);
    return {
      plateAnim: `plateAnim-${r}`,
      needleAnim: `needleAnim-${r}`,
    };
  }, []);

  const stroke = color;
  const foodOpacity = 0.3;
  const dropDur = `${(dropDurationMs / 1000).toFixed(3)}s`;
  const repeat = loop ? "indefinite" : 1;

  // Centro do mostrador semicírculo
  const cx = 100;
  const cy = 172;
  const r = 38;

  const renderScalePlateAnim = active ? (
    <animateTransform
      attributeName="transform"
      type="translate"
      values="0 0; 0 6; 0 0; 0 0"
      keyTimes="0;0.55;1;1"
      dur={dropDur}
      repeatCount={repeat}
      keySplines="0.2 0.8 0.2 1; 0.2 0.8 0.2 1; 0.2 0.8 0.2 1"
      calcMode="spline"
    />
  ) : null;

  const renderPlateAnim = active ? (
    <animateTransform
      id={ids.plateAnim}
      attributeName="transform"
      type="translate"
      values="0,-52; 0,0; 0,-52"
      keyTimes="0;0.55;1"
      dur={dropDur}
      repeatCount={repeat}
      keySplines="0.2 0.8 0.2 1; 0.2 0.8 0.2 1"
      calcMode="spline"
    />
  ) : null;

  const renderNeedleAnim = active ? (
    <animateTransform
    id={ids.needleAnim}
    attributeName="transform"
    type="rotate"
    additive="sum"
    values={`0 ${cx} ${cy}; 0 ${cx} ${cy}; 170 ${cx} ${cy}; 150 ${cx} ${cy}; 0 ${cx} ${cy}`}
    keyTimes="0; 0.45; 0.8; 0.95; 1"
    dur={dropDur}
    begin={`${ids.plateAnim}.begin+${(dropDurationMs * 0.55) / 1000}s`}
    repeatCount={repeat}
    calcMode="spline"
    keySplines="0.2 0.8 0.2 1; 0.2 0.8 0.2 1; 0.2 0.8 0.2 1; 0.2 0.8 0.2 1"
  />
  ) : null;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 200 210"
      role="img"
      aria-label="Balança pesando um prato"
      style={{ display: "block" }}
    >
      {/* ======= Prato (bowl) descendo ======= */}
      <g transform="translate(0,-52)">
        {renderPlateAnim}
        {/* comida */}
        <path
          d="M30 64 C38 52, 58 48, 74 58 C86 50, 110 50, 122 58 C140 48, 164 52, 170 64 L170 64 L30 64 Z"
          fill={color}
          opacity={foodOpacity}
        />
        {/* tigela */}
        <path
          d="M24 66 H176 C176 66, 174 78, 170 82 C164 88, 156 90, 100 90
             C44 90, 36 88, 30 82 C26 78, 24 66, 24 66 Z"
          fill="none"
          stroke={stroke}
          strokeWidth="3"
          strokeLinejoin="round"
        />
        {/* borda superior */}
        <path
          d="M20 62 H180"
          stroke={stroke}
          strokeWidth="4"
          strokeLinecap="round"
          fill="none"
        />
      </g>

      {/* ======= Balança (mais alta) ======= */}
      {/* plataforma da balança */}
      <g transform="translate(0,0)">
        {renderScalePlateAnim}
        <rect x="36" y="78" width="128" height="10" rx="2" fill={stroke} />
      </g>
      {/* coluna central */}
      <rect x="94" y="88" width="12" height="34" rx="2" fill={stroke} />

      {/* corpo (mais alto) */}
      <path
        d="M32 192 h136 v-60 a10 10 0 0 0 -10 -10 H42 a10 10 0 0 0 -10 10 z"
        fill="none"
        stroke={stroke}
        strokeWidth="3"
      />
      <path
        d="M24 200 a6 6 0 0 1 0 -12 h152 a6 6 0 0 1 0 12 z"
        fill={stroke}
      />

      {/* ======= Mostrador meia-lua ======= */}
      {/* fundo do semicírculo */}
      <path
        d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy} L ${
          cx - r
        } ${cy} Z`}
        fill="#fff"
        stroke={stroke}
        strokeWidth="3"
      />
      {/* marcas (3 principais) */}
      <g stroke={stroke} strokeWidth="3" strokeLinecap="round">
        {/* centro */}
        <line x1={cx} y1={cy - r + 6} x2={cx} y2={cy - r + 2} />
        {/* esquerda */}
        <line
          x1={cx - r * 0.86}
          y1={cy - r * 0.5}
          x2={cx - r * 0.72}
          y2={cy - r * 0.42}
        />
        {/* direita */}
        <line
          x1={cx + r * 0.86}
          y1={cy - r * 0.5}
          x2={cx + r * 0.72}
          y2={cy - r * 0.42}
        />
      </g>

      {/* ponteiro (pivot no centro da base do semicírculo) */}
      <g>
        <circle cx={cx} cy={cy} r="4.5" fill={stroke} />
        <g transform={`rotate(180 ${cx} ${cy})`}>
          {renderNeedleAnim}
          {/* agulha triangular */}
          <path
            d={`
              M ${cx - 2} ${cy - 2} 
              L ${cx - 2} ${cy + 2} 
              L ${cx + r - 6} ${cy}
              Z
            `}
            fill={stroke}
          />
        </g>
      </g>
    </svg>
  );
};

export default RestaurantScaleAnimation;
